import React, { useEffect, useMemo, useRef, useState } from "react";
import { City } from "country-state-city";
import {
  getCountryCodeByName,
  getStateCodeByName,
} from "../../Utils/countries";
import SelectField from "../SelectField";

interface CitySelectorProps {
  error?: string;
  value?: string;
  state?: string;
  country?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const CitySelector = ({
  error,
  onChange,
  state,
  country,
  value,
  placeholder,
  className,
}: CitySelectorProps) => {
  const [localValue, setLocalValue] = useState("");
  const previousCountry = useRef(country);
  const previousState = useRef(state);

  const countryCode = useMemo(
    () => getCountryCodeByName(country),
    [country]
  );

  const stateCode = useMemo(
    () => getStateCodeByName(countryCode, state),
    [countryCode, state]
  );

  const cities = useMemo(() => {
    if (!countryCode || !stateCode) return [];
    return City.getCitiesOfState(countryCode, stateCode);
  }, [countryCode, stateCode]);

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  useEffect(() => {
    if (previousCountry.current !== country || previousState.current !== state) {
      setLocalValue("");
      onChange("");
    }

    previousCountry.current = country;
    previousState.current = state;
  }, [country, state, onChange]);

  const handleChange = (nextValue: string) => {
    setLocalValue(nextValue);
    onChange(nextValue);
  };

  return (
    <SelectField
      showSearch
      label="City"
      error={error}
      disabled={!state}
      value={localValue}
      placeholder={placeholder ?? "Choose"}
      onChange={handleChange}
      data={cities.map((city) => ({ value: city.name }))}
      className={className}
    />
  );
};

export default CitySelector;
